import { useAuthContext } from "providers/Auth";
import { Links } from "./constants";

type AuthType = ReturnType<typeof useAuthContext>["type"];

// One predicate per conditionally-visible global link. A link with no entry
// here is visible to everyone, so ANY new gated entry in `globalLinks` must be
// added to this map or it shows up for every partner.
const GLOBAL_LINK_VISIBILITY: Record<string, (type: AuthType) => boolean> = {
  // Native chat lives in the Inbox and ships to BoomScreen-only partners too, so
  // surface the Inbox for them even without the BoomCRM communication features —
  // but only while chat is actually switched on, or they land on an Inbox with
  // no composer tab at all. Boom ops also get it before any flag is on, to
  // verify the chat migration.
  [Links.INBOX]: (type) => {
    // The Inbox is meaningless for a rent-reporting-only partner (no BoomCRM and
    // no BoomScreen), so hide it for everyone — Boom ops included.
    if (!type.isBoomCRM && !type.isBoomScreen) return false;
    // The free Core plan grants communication + communication_sms, so a free
    // partner reaches the SMS-only Inbox through the isBoomCRM arm (CRM-759).
    return (
      (type.isBoomCRM && (type.isCommunication || type.isCommunicationSms)) ||
      (type.isNativeChatActive && (type.isBoomScreen || type.isBoomCRM)) ||
      type.isBoomOps
    );
  },
  [Links.CONTACTS]: (type) => type.isBoomCRM,
  // Tasks is Pro-only, but the menu item stays for the whole CRM ecosystem: a
  // free partner clicking it gets the upgrade prompt instead of nothing at all
  // (CRM-759). The page itself paywalls when the entitlement is missing.
  [Links.TASKS]: (type) => type.isBoomCRM,
  // Analytics is always reachable; what a partner sees inside it follows the
  // products they have (PLT-634).
  // Provisioned numbers come with the free Core plan, so the item stays for the
  // whole CRM ecosystem and the page paywalls itself when the entitlement is
  // revoked — hiding it left /phone-numbers on a bare "Not found" (CRM-759).
  // Also shown to collections partners: outreach from the Outstanding balance
  // page needs a number to send from, and that page is granted by a UI flag
  // rather than the CRM plan (BF-552).
  [Links.PHONE_NUMBERS]: (type) =>
    type.isBoomCRM || type.isCollectionsOutstandingBalance,
  // Mirrors the /calls route gate (CRM-1235).
  [Links.CALLS]: (type) =>
    type.isCommunicationPhone && type.isProvisionedPhoneNumber,
  [Links.PROPERTY_GROUPS]: (type) =>
    !type.isPropertyManager && type.isBoomScreen,
  // Workflows are granted by the free Core plan (capped at one). When an admin
  // revokes the entitlement the item stays, like Tasks / Phone numbers above,
  // and the page paywalls itself (CRM-759 QA). The BoomScreen requirement
  // stays: workflows trigger on applications.
  [Links.WORKFLOWS]: (type) => type.isBoomCRM && type.isBoomScreen
};

export const shouldShowGlobalLink = (name: string, type: AuthType) =>
  GLOBAL_LINK_VISIBILITY[name]?.(type) ?? true;

export const shouldShowReportingLinks = (
  name: string,
  type: AuthType,
  partner: ReturnType<typeof useAuthContext>["partner"],
  pms: ReturnType<typeof useAuthContext>["pms"]
) => {
  const isAppfolioWithoutSupergood =
    pms.isAppfolio && !partner?.appfolio_supergood_enabled;

  const isBlockedRenter =
    name === Links.RENTERS &&
    (!type.isPMS ||
      (type.isBoomScreen && !type.isRentReporting) ||
      isAppfolioWithoutSupergood);

  return !isBlockedRenter;
};
